import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowUpRight, ArrowRight, Wheat, ShieldCheck, Layers, Globe2 } from "lucide-react";
import Reveal from "../components/Reveal";
import PageHeader from "../components/PageHeader";
import { PULSES_PRODUCTS } from "../data/catalogue";

const HIGHLIGHTS = [
  { icon: Wheat, title: "Whole & Split Formats", body: "From bulk whole beans to sortex-cleaned split dals — every SKU sized for both foodservice and retail." },
  { icon: ShieldCheck, title: "Rigorous Quality Control", body: "Sortex sorting, moisture control and lot-linked Certificates of Analysis on every shipment." },
  { icon: Layers, title: "Polished & Unpolished", body: "Available oil-polished, water-polished or unpolished per destination-market preference." },
  { icon: Globe2, title: "Global Grade Compliance", body: "EU MRL, FDA and Codex-aligned processing — with pesticide residue and heavy-metal reports on request." },
];

export default function Pulses() {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    if (!q.trim()) return PULSES_PRODUCTS;
    const s = q.toLowerCase();
    return PULSES_PRODUCTS.filter((p) => p.name.toLowerCase().includes(s) || p.tagline.toLowerCase().includes(s));
  }, [q]);

  return (
    <>
      <PageHeader
        eyebrow="Pulses & Lentils"
        title="Indian pulses, ready for the world's plate."
        italicWord="pulses"
        subtitle="Toor, chana, moong, urad, masoor, chickpeas, kidney beans and more — sortex cleaned, size-graded and packed for retail chains, plant-protein manufacturers and foodservice buyers worldwide."
      />

      {/* HIGHLIGHTS */}
      <section className="py-20 md:py-24 bg-brand-ivory">
        <div className="container-luxe">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {HIGHLIGHTS.map((h, i) => (
              <Reveal key={h.title} delay={i * 60} className="border-l border-brand-gold/40 pl-5 py-1">
                <div className="w-10 h-10 border border-brand-green/50 flex items-center justify-center mb-4 text-brand-green">
                  <h.icon className="w-4 h-4" />
                </div>
                <h4 className="font-serif text-xl text-brand-charcoal mb-2">{h.title}</h4>
                <p className="text-brand-charcoal/60 text-[14px] leading-relaxed">{h.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Filter Bar */}
      <section className="bg-brand-ivory sticky top-20 z-30 border-y border-brand-charcoal/10 backdrop-blur-md">
        <div className="container-luxe py-5 flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex items-center gap-2 flex-1">
            <Search className="w-4 h-4 text-brand-charcoal/40" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search pulses — toor, chana, rajma, chickpeas…"
              className="flex-1 bg-transparent border-0 outline-none text-[14px] py-2 placeholder-brand-charcoal/40"
              data-testid="pulses-search"
            />
          </div>
          <div className="text-[11px] tracking-[0.22em] uppercase text-brand-charcoal/50">{filtered.length} of {PULSES_PRODUCTS.length} pulses</div>
        </div>
      </section>

      {/* GRID */}
      <section className="py-16 md:py-24 bg-brand-ivory-warm">
        <div className="container-luxe">
          {filtered.length === 0 && (
            <div className="text-center py-24 text-brand-charcoal/50">No pulses matched your search.</div>
          )}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 lg:gap-8">
            {filtered.map((p, i) => (
              <Reveal key={p.slug} delay={i * 40}>
                <Link to={`/pulses/${p.slug}`} className="group block" data-testid={`pulses-card-${p.slug}`}>
                  <div className="relative aspect-[4/5] overflow-hidden bg-brand-ink">
                    <div className="absolute inset-0" style={{ backgroundColor: p.color, opacity: 0.35 }} />
                    <img src={p.image} alt={p.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.06]" />
                    <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/75 via-transparent to-transparent" />
                    <div className="absolute top-5 left-5 text-[10px] tracking-[0.3em] uppercase text-brand-ivory/80 border border-brand-ivory/30 px-2.5 py-1">
                      {p.grade.split("/")[0].trim()}
                    </div>
                    <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between text-brand-ivory">
                      <div>
                        <div className="font-serif text-xl leading-tight">{p.name}</div>
                        <div className="text-[10px] tracking-[0.22em] uppercase text-brand-ivory/60 italic mt-1">{p.tagline}</div>
                      </div>
                      <ArrowUpRight className="w-5 h-5 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </div>
                  <div className="mt-4 flex items-center justify-between text-[12px] tracking-wide">
                    <span className="text-brand-charcoal/60">MOQ · {p.moq}</span>
                    <span className="text-brand-green font-semibold uppercase tracking-[0.2em] text-[11px]">View Details</span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-20 text-center">
            <p className="text-brand-charcoal/65 max-w-xl mx-auto">Bulk supply, private label packaging and destination-specific quality reports available across every SKU.</p>
            <Link to="/request-quote" className="mt-8 luxe-btn-primary inline-flex" data-testid="pulses-cta">Request Quote <ArrowRight className="w-4 h-4" /></Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
